import React, { useState, useCallback, useEffect, useRef } from 'react';
import shallow from 'zustand/shallow';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import native from '@root/native';
import Toast from '@root/components/toast';
import Modal from '@root/components/modal';
import { isInApp } from '@root/shared';
import { subscriptionApi } from '@api';
import { IPackageDetail } from '@root/types/package';
import {
  DATE_FORMAT,
  HOUSE_ID,
  UNLIMITED_PACKAGE_NUMBER,
} from '@root/constant';
import { ILoading } from '@root/types/common';
import {
  useHouseState,
  useSubscriptionDetailState,
  useSubscriptionsState,
} from '@root/state';
import { EAppChangeStatus } from '@root/native/types';

const useSubscriptionDetailData = (
  id?: string,
): {
  stateId?: string;
  detail: IPackageDetail;
  loading: ILoading;
  getDetail: (cb?: () => void) => void;
} => {
  const navigate = useNavigate();
  const setCurrentHouseId = useHouseState((state) => state.setCurrentHouseId);
  const { detail, loading, stateId, updateTime, setLoading, setDetail } =
    useSubscriptionDetailState(
      (state) => ({
        loading: state.loading,
        detail: state.detail,
        stateId: state.id,
        updateTime: state.updateTime,
        setLoading: state.setLoading,
        setDetail: state.setDetail,
      }),
      shallow,
    );

  const getDetail = useCallback(
    (cb?: () => void) => {
      if (!id) {
        return;
      }

      setLoading(0);

      subscriptionApi
        .getSubscriptionDetail(id)
        .then((data: any) => {
          const isUnlimited = data?.buyNum === UNLIMITED_PACKAGE_NUMBER;
          setDetail({ ...data, isUnlimited }, id);
          if (!localStorage.getItem(HOUSE_ID)) {
            if (data.homeId) {
              localStorage.setItem(HOUSE_ID, data.homeId);
              setCurrentHouseId(data.homeId);
            } else {
              navigate('/');
            }
          }
          setLoading(1);
        })
        .catch(() => {
          setLoading(2);
        })
        .finally(() => {
          if (cb) {
            cb();
          }
        });
    },
    [id, setLoading, setDetail, setCurrentHouseId, navigate],
  );

  useEffect(() => {
    if (!id) {
      return;
    }

    if (id !== stateId || new Date().getTime() - updateTime > 60 * 1000) {
      getDetail();
    }
  }, [getDetail, id, stateId, updateTime]);

  return {
    stateId,
    detail,
    loading: id && id === stateId ? 1 : loading,
    getDetail,
  };
};

export interface IDetailState {
  detail: IPackageDetail;
  loading: number;
  requesting: boolean;
  onSwitchRenewal: () => void;
  getDetail: (cb?: () => void) => void;
  onAdjust: () => void;
  onChangePackage: () => void;
  // onApplePaySwitchRenewal: () => void;
  onApplePayCheckHandle: () => void;
}

const useSubscriptionDetail = (): IDetailState => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const expiredSubscriptionList = useSubscriptionsState(
    (state) => state.setExpiredData,
  );
  const { loading, detail, getDetail } = useSubscriptionDetailData(id);
  const [requesting, setRequesting] = useState(false);
  const {
    goodsStatus,
    whetherPay,
    subScribeStatus,
    isRenewal,
    nextRenewal,
    planUnit,
    level,
  } = detail;
  const getDetailRef = useRef(getDetail);
  getDetailRef.current = getDetail;

  const turnOnOff = useCallback(
    (status: number) => {
      if (!id) {
        return;
      }
      setRequesting(true);
      subscriptionApi
        .updateSubscribeStatus({
          packageId: id,
          subscribeStatus: status,
        })
        .then((data: any) => {
          // 首页数据为过期数据
          expiredSubscriptionList();
          const { status: planStatus, lock } = data;
          if (lock) {
            getDetail();
            Modal.alert({
              content: planStatus
                ? t('renewal_lock_turn_off')
                : t('renewal_lock_turn_on'),
              confirm: t('ok'),
            });
            setRequesting(false);
            return;
          }

          // 更新详情
          getDetail(() => {
            setRequesting(false);
            Toast.info(t('operation_successful'));
          });
        })
        .catch(() => {
          setRequesting(false);
          Toast.info(t('operation_failed'));
        });
    },
    [expiredSubscriptionList, getDetail, id, t],
  );

  const onSwitchRenewal = useCallback(() => {
    if (requesting) {
      return;
    }

    if (goodsStatus < 1) {
      Modal.alert({
        content: t('change_plan_tips_9'),
        confirm: t('ok'),
      });
      return;
    }

    if (!whetherPay) {
      navigate(`/giftCard/renewal/${id}`);
      return;
    }

    const _isRenewal = isRenewal || subScribeStatus === 1;

    Modal.confirm({
      id: 'packages-renewal',
      title: _isRenewal ? t('turn_off') : t('turn_on'),
      content: _isRenewal
        ? t('change_plan_tips_4', {
            date: nextRenewal ? dayjs(nextRenewal).format(DATE_FORMAT) : '',
          })
        : t('change_plan_tips_5'),
      cancel: t('cancel'),
      confirm: t('ok'),
      onConfirm: () => {
        turnOnOff(_isRenewal ? 0 : 1);
      },
      // onCancel: () => {
      // },
    });
  }, [
    goodsStatus,
    id,
    isRenewal,
    navigate,
    nextRenewal,
    requesting,
    subScribeStatus,
    t,
    turnOnOff,
    whetherPay,
  ]);

  // const onApplePaySwitchRenewal = useCallback(() => {
  //   if (detail.subScribeStatus !== 1) {
  //     return;
  //   }

  //   Modal.confirm({
  //     id: 'packages-renewal',
  //     content: t('apple_pay_turn_off_tips1'),
  //     cancel: t('keep_the_service'),
  //     confirm: t('still_want_turn_off'),
  //     footerColumn: true,
  //     onConfirm: () => {
  //       setTimeout(() => {
  //         Modal.confirm({
  //           id: 'packages-renewal',
  //           content: t('apple_pay_turn_off_tips2'),
  //           cancel: t('maybe_later'),
  //           confirm: t('ok_i_see'),
  //           // footerColumn: true,
  //           onConfirm: () => {
  //             native.system.openThirdApp({
  //               marketUrl: 'https://apps.apple.com/account/subscriptions',
  //             });
  //           },
  //         });
  //       }, 200);
  //     },
  //   });
  // }, [detail.subScribeStatus, t]);

  const onApplePayDowngrade = useCallback(() => {
    setTimeout(() => {
      Modal.confirm({
        id: 'packages-downgrade',
        content: t('Downgrade_Plan_Tip'),
        cancel: t('keep_the_service'),
        confirm: t('Still_Want_to_Downgrade'),
        footerColumn: true,
        onConfirm: () => {
          native.system.openThirdApp({
            marketUrl: 'https://apps.apple.com/account/subscriptions',
          });
        },
      });
    }, 200);
  }, [t]);

  const onApplePayCheckHandle = useCallback(() => {
    const actions: { key: 'DOWN' | 'UP'; value: string }[] = [];

    if (detail.canDowngrade) {
      actions.push({
        key: 'DOWN',
        value: t('Downgrade_Plan'),
      });
    }

    if (detail.canUpgrade) {
      actions.push({
        key: 'UP',
        value: t('Upgrade_Plan'),
      });
    }

    Modal.confirm({
      id: 'packages-check',
      wrapClassName: 'apple-check-confirm',
      cancel: t('Cancel'),
      confirm: t('Check_Subscription'),
      actions: actions.map((i) => i.value),
      footerColumn: true,
      onConfirm: () => {
        native.system.openThirdApp({
          marketUrl: 'https://apps.apple.com/account/subscriptions',
        });
      },
      onActions: (index: number) => {
        const isUpgrade = actions[index].key === 'UP';
        if (isUpgrade) {
          // 升级
          navigate('/applePay');
        } else {
          // 降级
          onApplePayDowngrade();
        }
      },
    });
  }, [
    detail.canDowngrade,
    detail.canUpgrade,
    navigate,
    onApplePayDowngrade,
    t,
  ]);

  const onAdjust = useCallback(() => {
    navigate(`/subscription/${id}/adjustDevice`);
  }, [navigate, id]);

  const onChangePackage = useCallback(() => {
    navigate(
      `/packages?changePackageId=${id}&isSupportRenew=1&excludeLevel=${level}&planUnit=${planUnit}`,
    );
  }, [level, navigate, id, planUnit]);

  useEffect(() => {
    if (!isInApp) {
      return;
    }

    const onActiveChange = ({ status }: { status: EAppChangeStatus }) => {
      if (status === EAppChangeStatus.DidBecomeActive) {
        getDetailRef.current();
      }
    };
    native.system.onActiveChange(onActiveChange);
    return () => {
      native.system.offActiveChange(onActiveChange);
    };
  }, []);

  useEffect(() => {
    return () => {
      Toast.destroy();
      Modal.close();
    };
  }, []);

  return {
    detail,
    loading,
    requesting,
    onSwitchRenewal,
    getDetail,
    onAdjust,
    onChangePackage,
    onApplePayCheckHandle,
    // onApplePaySwitchRenewal,
  };
};

export { useSubscriptionDetailData };

export default useSubscriptionDetail;
